import React from 'react';
import styled from 'styled-components';

import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { theme } from '../../theme';

interface IDropdown {
	trigger: React.FunctionComponentElement<{ active?: boolean }>;
	children: React.ReactNode;
	closeOnItemClick?: boolean;
	dropdownAlignment?: 'center' | 'flex-end' | 'flex-start';
	width?: string;
}
interface IDropdownContainer {
	isOpen: boolean;
	top: string;
	width?: string;
}

interface IMainContainer {
	alignment: string;
}

const TriggerContainer = styled.div`
	display: inline;
`;

const MainContainer = styled.div<IMainContainer>`
	position: relative;
	display: inline-flex;
	flex-direction: column;
	align-items: ${({ alignment }) => alignment};
`;

const DropdownContainer = styled.div<IDropdownContainer>`
	position: absolute;
	display: flex;
	top: ${({ top }) => top};
	visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
	opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
	transition: all 150ms ease-in;
	flex-direction: column;
	background-color: ${theme.colors.dropdown.background};
	width: ${({ width }) => width || '240px'};
	padding: 8px;
	border-radius: 4px;
	box-shadow: 0 32px 40px -2px rgba(10, 22, 70, 0.12), 0 0 1px 0 rgba(10, 22, 70, 0.06);
`;

export const Dropdown = ({ trigger, children, closeOnItemClick, width, dropdownAlignment }: IDropdown) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const [dropdownPositionTopValue, setDropdownPositionTopValue] = React.useState('8px');

	const onClickOutside = (target: Node) => {
		if (triggerContainerRef.current && triggerContainerRef.current.contains(target)) {
			return;
		}
		if (isOpen) {
			setIsOpen(false);
		}
	};

	const triggerContainerRef = React.useRef<HTMLDivElement>(null);
	const dropdownContainerRef = React.useRef<HTMLDivElement>(null);
	useOnClickOutside(dropdownContainerRef, (target: Node) => onClickOutside(target));

	const onTriggerClick = () => {
		setIsOpen(!isOpen);
	};

	React.useEffect(() => {
		if (triggerContainerRef.current) {
			// calculates the position of the dropdown. + 8 is for the space between the dropdown and the trigger
			setDropdownPositionTopValue(triggerContainerRef.current.clientHeight + 8 + 'px');
		}
	}, [triggerContainerRef]);

	return (
		<React.Fragment>
			<MainContainer alignment={dropdownAlignment || 'center'}>
				<TriggerContainer ref={triggerContainerRef} onClick={onTriggerClick}>
					{React.cloneElement(trigger, { active: isOpen })}
				</TriggerContainer>
				<DropdownContainer ref={dropdownContainerRef} isOpen={isOpen} width={width} top={dropdownPositionTopValue}>
					<span onClick={() => (closeOnItemClick === true || closeOnItemClick === undefined) && setIsOpen(false)}>
						{children}
					</span>
				</DropdownContainer>
			</MainContainer>
		</React.Fragment>
	);
};
